import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Building2, Users, Upload, ImageOff } from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { Modal } from "../../../components/ui/Modal";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { AddBusinessModal } from "./AddBusinessModal";
import { BusinessMembersModal } from "./BusinessMembersModal";
import { useBusinesses, useUpdateBusiness, useUploadBusinessLogo } from "../../../hooks/useBusinesses";
import { ApiError } from "../../../lib/api";
import type { Business } from "../../../types/index";

const editSchema = z.object({
  name: z.string().min(1, "Required").max(100),
  address: z.string().min(1, "Required").max(200),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  coeCompanyName: z.string().max(150).optional(),
});
type EditValues = z.infer<typeof editSchema>;

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-[#dc2626] text-xs mt-1";

export default function BusinessesPage() {
  const { toasts, toast, dismissToast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [editBusiness, setEditBusiness] = useState<Business | null>(null);
  const [membersBusiness, setMembersBusiness] = useState<Business | null>(null);

  const { data, isLoading, error } = useBusinesses();
  const businesses = data?.items ?? [];

  const total = businesses.length;
  const active = businesses.filter((b) => b.status === "ACTIVE").length;
  const inactive = businesses.filter((b) => b.status === "INACTIVE").length;

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0f172a]">Manage Businesses</h1>
          <p className="mt-0.5 text-sm text-[#64748b]">
            Create and manage tenant businesses. Assign admins and control access per business.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Business
        </Button>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        <StatBadge label="Total" value={total} color="blue" />
        <StatBadge label="Active" value={active} color="green" />
        <StatBadge label="Inactive" value={inactive} color="gray" />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-[#fca5a5] bg-[#fef2f2] p-4 text-sm text-[#dc2626]">
          Failed to load businesses. Please try again.
        </div>
      ) : businesses.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] bg-white py-16 text-center">
          <Building2 className="h-10 w-10 text-[#cbd5e1] mb-3" />
          <p className="text-sm font-medium text-[#64748b]">No businesses yet</p>
          <p className="text-xs text-[#94a3b8] mt-1">Create the first business to get started.</p>
          <Button onClick={() => setAddOpen(true)} className="mt-4 gap-2">
            <Plus className="h-4 w-4" />
            Add Business
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#f1f5f9] bg-[#f8fafc]">
                <th className="px-5 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                  Business
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide hidden md:table-cell">
                  Address
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                  Status
                </th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((b) => (
                <tr
                  key={b.businessId}
                  className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eff2ff]">
                        <Building2 className="h-4 w-4 text-[#2447d9]" />
                      </div>
                      <span className="font-medium text-[#0f172a]">{b.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[#64748b] hidden md:table-cell max-w-[220px] truncate">
                    {b.address}
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        b.status === "ACTIVE"
                          ? "bg-[#dcfce7] text-[#16a34a]"
                          : "bg-[#f1f5f9] text-[#64748b]"
                      }`}
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setMembersBusiness(b)}
                        className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#2447d9] transition-colors"
                        aria-label={`View members of ${b.name}`}
                      >
                        <Users className="h-3.5 w-3.5" />
                        Members
                      </button>
                      <button
                        onClick={() => setEditBusiness(b)}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#2447d9] transition-colors"
                        aria-label={`Edit ${b.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      <AddBusinessModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={() => {
          setAddOpen(false);
          toast.success("Business created");
        }}
      />

      {/* Edit modal */}
      {editBusiness && (
        <EditBusinessModal
          business={editBusiness}
          onClose={() => setEditBusiness(null)}
          onSuccess={() => {
            setEditBusiness(null);
            toast.success("Business updated");
          }}
        />
      )}

      {/* Members modal */}
      {membersBusiness && (
        <BusinessMembersModal
          business={membersBusiness}
          isOpen={true}
          onClose={() => setMembersBusiness(null)}
        />
      )}
    </div>
  );
}

function StatBadge({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: "blue" | "green" | "gray";
}) {
  const colors = {
    blue: "bg-[#eff2ff] text-[#2447d9]",
    green: "bg-[#dcfce7] text-[#16a34a]",
    gray: "bg-[#f1f5f9] text-[#64748b]",
  };
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${colors[color]}`}>
      {label}: {value}
    </span>
  );
}

function EditBusinessModal({
  business,
  onClose,
  onSuccess,
}: {
  business: Business;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const updateBusiness = useUpdateBusiness(business.businessId);
  const uploadLogo = useUploadBusinessLogo(business.businessId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<{ data: string; type: string } | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: business.name,
      address: business.address,
      status: business.status,
      coeCompanyName: business.coeCompanyName ?? "",
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setLogoError("Only PNG and JPEG images are supported.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Logo must be under 2 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setLogoPreview(result);
      // Strip the data URL prefix to get base64 only
      const base64 = result.split(",")[1] ?? "";
      setLogoFile({ data: base64, type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async (data: EditValues) => {
    try {
      await updateBusiness.mutateAsync({
        ...data,
        coeCompanyName: data.coeCompanyName?.trim() || undefined,
      });
      if (logoFile) {
        await uploadLogo.mutateAsync({ imageData: logoFile.data, contentType: logoFile.type });
      }
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setError("root", { message: err.message });
      } else {
        setError("root", { message: "Something went wrong. Please try again." });
      }
    }
  };

  const hasLogo = !!business.logoUrl;
  const isBusy = isSubmitting || uploadLogo.isPending;

  return (
    <Modal isOpen={true} onClose={onClose} title="Edit Business" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className={LABEL_CLASS}>Business Name</label>
          <input {...register("name")} className={INPUT_CLASS} autoFocus />
          {errors.name && <p className={ERROR_CLASS}>{errors.name.message}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS}>Address</label>
          <textarea {...register("address")} className={`${INPUT_CLASS} resize-none`} rows={3} />
          {errors.address && <p className={ERROR_CLASS}>{errors.address.message}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS}>COE Company Name</label>
          <input
            {...register("coeCompanyName")}
            className={INPUT_CLASS}
            placeholder={business.name}
          />
          <p className="text-[10px] text-[#94a3b8] mt-1">
            Used in the Certificate of Employment PDF. Leave blank to use the business name above.
          </p>
          {errors.coeCompanyName && <p className={ERROR_CLASS}>{errors.coeCompanyName.message}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS}>Status</label>
          <select {...register("status")} className={INPUT_CLASS}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          {errors.status && <p className={ERROR_CLASS}>{errors.status.message}</p>}
        </div>

        {/* Logo upload */}
        <div>
          <label className={LABEL_CLASS}>Business Logo</label>
          <p className="text-xs text-[#94a3b8] mb-2">
            Displayed in payslip and COE PDF headers. PNG or JPEG, max 2 MB.
          </p>
          <div className="flex items-start gap-3">
            {/* Preview box */}
            <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo preview" className="max-h-14 max-w-[104px] object-contain" />
              ) : hasLogo ? (
                <span className="text-xs text-[#64748b] text-center px-1">Logo set</span>
              ) : (
                <ImageOff className="h-6 w-6 text-[#cbd5e1]" />
              )}
            </div>
            <div className="flex-1 space-y-1.5">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
              >
                <Upload className="h-3.5 w-3.5" />
                {hasLogo || logoFile ? "Replace Logo" : "Upload Logo"}
              </button>
              {logoFile && (
                <p className="text-xs text-[#16a34a]">New logo selected — will upload on save.</p>
              )}
              {logoError && <p className={ERROR_CLASS}>{logoError}</p>}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {errors.root && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">
            {errors.root.message}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isBusy}
            className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60"
          >
            {isBusy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
